import { useMemo } from 'react';
import { Box, Typography } from '@mui/material';
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from 'recharts';
import { PointhomeCashRevenueItem } from '../../../types/pointhome.types';

const REVENUE_COLOR = '#64B5F6';
const NET_REVENUE_COLOR = '#81C784';
const REVENUE_GRADIENT_ID = 'colorPointhomeRevenue';
const NET_REVENUE_GRADIENT_ID = 'colorPointhomeNetRevenue';

type Props = {
  data: PointhomeCashRevenueItem[];
  title: string;
};

export default function PointhomeRevenueDashboard({ data, title }: Props) {
  const chartData = useMemo(() => {
    const byDate: Record<string, { date: string; revenue: number; netRevenue: number }> = {};
    data.forEach((row) => {
      if (!byDate[row.date]) {
        byDate[row.date] = { date: row.date, revenue: 0, netRevenue: 0 };
      }
      byDate[row.date].revenue += row.revenue;
      byDate[row.date].netRevenue += row.netRevenue;
    });
    return Object.values(byDate).sort((a, b) => a.date.localeCompare(b.date));
  }, [data]);

  return (
    <Box
      sx={{
        backgroundColor: '#ffffff',
        borderRadius: 3,
        p: 3,
        mt: 3,
        border: '1px solid #f1f5f9',
      }}
    >
      <Typography variant="subtitle1" fontWeight={600} mb={2} textAlign="center" color="#475569">
        {title}
      </Typography>
      <ResponsiveContainer width="100%" height={280}>
        <AreaChart data={chartData} margin={{ top: 8, right: 24, left: 8, bottom: 8 }}>
          <defs>
            <linearGradient id={REVENUE_GRADIENT_ID} x1="0" y1="0" x2="0" y2="1">
              <stop offset="5%" stopColor={REVENUE_COLOR} stopOpacity={0.25} />
              <stop offset="95%" stopColor={REVENUE_COLOR} stopOpacity={0} />
            </linearGradient>
            <linearGradient id={NET_REVENUE_GRADIENT_ID} x1="0" y1="0" x2="0" y2="1">
              <stop offset="5%" stopColor={NET_REVENUE_COLOR} stopOpacity={0.25} />
              <stop offset="95%" stopColor={NET_REVENUE_COLOR} stopOpacity={0} />
            </linearGradient>
          </defs>
          <CartesianGrid strokeDasharray="3 3" stroke="#e0e0e0" vertical={false} />
          <XAxis dataKey="date" tick={{ fontSize: 12 }} />
          <YAxis
            tickFormatter={(v) =>
              v >= 10000 ? `${(v / 10000).toFixed(0)}만` : v.toLocaleString('ko-KR')
            }
            tick={{ fontSize: 11 }}
            label={{ value: '매출', angle: -90, position: 'insideLeft', style: { fontSize: 12 } }}
          />
          <Tooltip
            content={({ active, payload }) => {
              if (!active || !payload?.length) return null;
              const row = payload[0]?.payload as {
                date: string;
                revenue: number;
                netRevenue: number;
              };
              if (!row) return null;
              return (
                <Box
                  sx={{
                    p: 1.5,
                    minWidth: 160,
                    bgcolor: 'background.paper',
                    borderRadius: 1,
                    border: '1px solid',
                    borderColor: 'divider',
                    boxShadow: 2,
                  }}
                >
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ mb: 1, fontWeight: 600 }}
                  >
                    {row.date}
                  </Typography>
                  <Typography variant="body2">
                    매출: ₩{row.revenue.toLocaleString('ko-KR', { maximumFractionDigits: 0 })}
                  </Typography>
                  <Typography variant="body2">
                    순 매출: ₩{row.netRevenue.toLocaleString('ko-KR', { maximumFractionDigits: 0 })}
                  </Typography>
                </Box>
              );
            }}
          />
          <Area
            type="monotone"
            dataKey="revenue"
            stroke={REVENUE_COLOR}
            strokeWidth={2}
            fill={`url(#${REVENUE_GRADIENT_ID})`}
            fillOpacity={1}
            name="매출"
          />
          <Area
            type="monotone"
            dataKey="netRevenue"
            stroke={NET_REVENUE_COLOR}
            strokeWidth={2}
            fill={`url(#${NET_REVENUE_GRADIENT_ID})`}
            fillOpacity={1}
            name="순 매출"
          />
        </AreaChart>
      </ResponsiveContainer>
    </Box>
  );
}
